import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { AllHTMLAttributes, FocusEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import classNames from 'classnames';
import { atoms } from '../../css/atoms/atoms.css';

import { Box } from '../Box/Box';
import { FieldMessage } from '../FieldMessage/FieldMessage';

import { TextFieldProps } from '../TextField/TextField';
import { ClearButton } from '../TextField/ClearButton';
import { Adornment } from '../TextField/Adornment';

import {
  wrapperStyle,
  labelStyle,
  inputStyle,
  fieldsetStyle,
  legendStyle,
  spanStyle,
  defaultHoverStyle,
  criticalHoverStyle,
  cautionHoverStyle,
} from '../TextField/TextField.css';

import { textAreaStyle, textAreaWrapper } from './TextArea.css';

type BaseTextAreaProps = AllHTMLAttributes<HTMLTextAreaElement>;

export interface TextAreaProps
  extends Omit<
    TextFieldProps,
    'type' | 'onChange' | 'onBlur' | 'onPaste' | 'onFocus' | 'onKeyDown' | 'onKeyUp' | 'adornmentPosition'
  > {
  /**
   * Controls the visible height of the text area in lines
   * */
  rows?: number;
  onChange: NonNullable<BaseTextAreaProps['onChange']>;
  onBlur?: BaseTextAreaProps['onBlur'];
  onPaste?: BaseTextAreaProps['onPaste'];
  onFocus?: BaseTextAreaProps['onFocus'];
  onKeyDown?: BaseTextAreaProps['onKeyDown'];
  onKeyUp?: BaseTextAreaProps['onKeyUp'];

  /**
   * Use this prop to provide a position of the adornment. Default position is `right`.
   */
  adornmentPosition?: 'top-right' | 'right';
}

/**
 * `TextArea`
 *
 * Use this component if you want to support a free form, multi-line text field.
 *
 * `TextArea` uses largely the same props as `TextField` but maps to a textarea element
 *
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      id,
      value,
      onClear,
      onBlur,
      onFocus,
      label,
      message,
      tone,
      disabled,
      adornment,
      adornmentPosition = 'top-right',
      size = 'medium',
      ...restProps
    },
    ref
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    const [focused, setFocused] = useState(false);

    const handleOnFocus = (event: FocusEvent<HTMLTextAreaElement>) => {
      setFocused(true);
      if (onFocus) {
        onFocus(event);
      }
    };

    const handleOnBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
      setFocused(false);
      if (onBlur) {
        onBlur(event);
      }
    };

    const focusTextArea = () => {
      if (innerRef.current) {
        innerRef.current.focus();
      }
    };

    const hasValue = typeof value === 'string' && value.length > 0;

    const hoverStyle = useMemo(() => {
      switch (true) {
        case focused || disabled:
          return undefined;
        case tone === 'critical':
          return criticalHoverStyle;
        case tone === 'caution':
          return cautionHoverStyle;
        default:
          return defaultHoverStyle;
      }
    }, [focused, disabled, tone]);

    return (
      <Box display="flex" flexDirection="column">
        <div className={wrapperStyle}>
          {label && (
            <label
              htmlFor={id}
              className={labelStyle({
                disabled,
                focused: focused || hasValue,
              })}
            >
              {label}
            </label>
          )}
          <div className={hoverStyle}>
            <Box
              className={textAreaWrapper({
                disabled,
              })}
              onClick={focusTextArea}
            >
              <textarea
                id={id}
                value={value}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                className={classNames(
                  inputStyle({
                    size,
                  }),
                  textAreaStyle
                )}
                disabled={disabled}
                ref={innerRef}
                {...restProps}
              />
            </Box>
            <fieldset
              className={fieldsetStyle({
                focused,
                tone,
              })}
            >
              <legend className={legendStyle({ focused: focused || hasValue })}>
                {label && <span className={spanStyle}>{label}</span>}
              </legend>
            </fieldset>
          </div>

          {/* Clear Button */}
          <AnimatePresence mode="wait">
            {onClear && value && (
              <Adornment position="right">
                {
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ClearButton onClick={onClear} />
                  </motion.div>
                }
              </Adornment>
            )}
          </AnimatePresence>

          {/* End Adornment */}
          {!onClear && adornment && <Adornment position={adornmentPosition}>{adornment}</Adornment>}
        </div>
        {message && (
          <span
            className={atoms({
              paddingTop: 'xsmall',
            })}
          >
            <FieldMessage id={id} tone={tone} message={message} />
          </span>
        )}
      </Box>
    );
  }
);

TextArea.displayName = 'TextArea';
